/**
 * The "Employee Lifeline" menu item: choose a company, then an employee (search by name or
 * employee number), then open their lifeline. Every person comes with the server's opaque
 * reference, and only that goes in the URL. Active people by default; "Include former
 * employees" widens the list. Name, number, company, status and job title only: no pay.
 */
import { useEffect, useRef, useState } from 'react'
import { getLifelineCompanies, searchLifelineEmployees } from '../../../api'
import LoadError from '../../../components/LoadError'
import './Lifeline.css'

const STATUS = { A: 'Active', L: 'On leave', S: 'Suspended', T: 'Former' }
const SEARCH_DELAY_MS = 250

export default function EmployeePicker({ customerId, onOpen, onClose }) {
  const [companies, setCompanies] = useState(null)
  const [companiesError, setCompaniesError] = useState(null)
  const [companiesTry, setCompaniesTry] = useState(0)
  const [company, setCompany] = useState('')
  const [q, setQ] = useState('')
  const [includeFormer, setIncludeFormer] = useState(false)
  const [result, setResult] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [searchTry, setSearchTry] = useState(0)
  const seq = useRef(0)

  useEffect(() => {
    let live = true
    setCompanies(null)
    setCompaniesError(null)
    getLifelineCompanies(customerId)
      .then(d => {
        if (!live) return
        const list = Array.isArray(d?.companies) ? d.companies : null
        if (!list) { setCompaniesError(new Error('Unexpected response: no companies')); return }
        setCompanies(list)
        if (list.length === 1) setCompany(list[0].company_ref)
      })
      .catch(e => { if (live) setCompaniesError(e) })
    return () => { live = false }
  }, [customerId, companiesTry])

  useEffect(() => {
    if (!company) { setResult(null); return undefined }
    const mine = ++seq.current
    setLoading(true)
    setError(null)
    const t = setTimeout(() => {
      searchLifelineEmployees(customerId, company, q.trim(), includeFormer)
        .then(d => { if (seq.current === mine) setResult(d) })
        .catch(e => { if (seq.current === mine) { setError(e); setResult(null) } })
        .finally(() => { if (seq.current === mine) setLoading(false) })
    }, SEARCH_DELAY_MS)
    return () => clearTimeout(t)
  }, [customerId, company, q, includeFormer, searchTry])

  return (
    <div className="ll-root">
      <button type="button" className="ll-back" onClick={onClose}>← Back to the portal</button>
      <header className="ll-header">
        <div className="ll-who">
          <div className="ll-eyebrow">Employee Lifeline</div>
          <h1 className="ll-name">Choose an employee</h1>
          <p className="ll-na">Pick a company, then search by name or employee number.</p>
        </div>
      </header>

      <section className="ll-card ll-picker" aria-label="Choose an employee">
        {companiesError && (
          <LoadError what="the companies" error={companiesError} onRetry={() => setCompaniesTry(n => n + 1)} />
        )}
        {!companies && !companiesError && <div className="ll-loading" role="status"><div className="dm-spinner" />Loading companies…</div>}
        {companies && (
          <div className="ll-picker-controls">
            <label className="ll-field">
              <span className="ll-eyebrow">Company</span>
              <select value={company} onChange={e => setCompany(e.target.value)}>
                <option value="">Select a company…</option>
                {companies.map(c => (
                  <option key={c.company_ref} value={c.company_ref}>
                    {[c.company_name, c.company_code].filter(Boolean).join(' · ')}{c.people ? ` (${c.people.toLocaleString()})` : ''}
                  </option>
                ))}
              </select>
            </label>
            <label className="ll-field ll-field--grow">
              <span className="ll-eyebrow">Employee</span>
              <input type="search" value={q} onChange={e => setQ(e.target.value)} disabled={!company}
                     placeholder={company ? 'Name or employee number' : 'Select a company first'} maxLength={80}
                     autoComplete="off" />
            </label>
            <label className="ll-check">
              <input type="checkbox" checked={includeFormer} onChange={e => setIncludeFormer(e.target.checked)} disabled={!company} />
              Include former employees
            </label>
          </div>
        )}

        {company && error && <LoadError inline what="employees" error={error} onRetry={() => setSearchTry(n => n + 1)} />}
        {company && loading && !result && <div className="ll-loading" role="status"><div className="dm-spinner" />Searching…</div>}
        {company && result && result.available === false && <p className="ll-na">{result.reason}</p>}
        {company && result?.available && (
          <div aria-live="polite" aria-busy={loading}>
            <div className="ll-note">
              {result.matches === 0 ? 'No one matches.' : `${result.matches.toLocaleString()} match${result.matches === 1 ? '' : 'es'}`}
              {result.matches > result.people.length ? ` · showing the first ${result.people.length}; narrow the search to see others` : ''}
              {result.truncated ? ' · this company is larger than the list can hold; search by number to be sure' : ''}
            </div>
            <ul className="ll-people">
              {result.people.map(p => (
                <li key={p.ref}>
                  <button type="button" className="ll-person" onClick={() => onOpen(p.ref)}>
                    <span className="ll-person-name">{[p.first_name, p.last_name].filter(Boolean).join(' ') || 'Name not recorded'}</span>
                    <span className="ll-person-meta ll-mono">
                      {p.employee_number}{p.job_title ? ` · ${p.job_title}` : ''}
                    </span>
                    <span className={`ll-pill ll-pill--${p.status || 'A'}`}><span className="ll-pill-dot" />{STATUS[p.status] || p.status || '—'}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>
    </div>
  )
}
